import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons";
import { stableStreamColor } from "../spec/stable-stream-color";
import { groupDisplayedStreams } from "./group-picker-streams";
import { type PickerRowText, pickerRowTexts } from "./picker-row-text";
import { GroupSection, ROW_CLASS, RowText, StatusLine } from "./picker-rows";
import type { PickerStream } from "./picker-stream";

const RemoveIcon = iconRegistry[ICON_KEYS.CLOSE];

export function DisplayedTab({
    plotted,
    addedIds,
    onRemove,
}: {
    plotted: readonly PickerStream[];
    addedIds: ReadonlySet<number>;
    onRemove: (streamId: number) => void;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const { t: tMessages } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "messages" });
    const thisZone = t("this-zone");
    const noZone = t("no-zone");

    const { zoneStreams, addedByZone } = useMemo(
        () => groupDisplayedStreams(plotted, addedIds, { thisZone, noZone }),
        [plotted, addedIds, thisZone, noZone],
    );

    return (
        <>
            {addedByZone.map((group) => (
                <DisplayedGroup
                    key={group.key}
                    label={group.label}
                    streams={group.streams}
                    addedIds={addedIds}
                    onRemove={onRemove}
                />
            ))}
            <DisplayedGroup
                label={thisZone}
                streams={zoneStreams}
                addedIds={addedIds}
                onRemove={onRemove}
                emptyText={tMessages("empty")}
            />
        </>
    );
}

function DisplayedGroup({
    label,
    streams,
    addedIds,
    onRemove,
    emptyText,
}: {
    label: string;
    streams: readonly PickerStream[];
    addedIds: ReadonlySet<number>;
    onRemove: (streamId: number) => void;
    emptyText?: string;
}) {
    const texts = pickerRowTexts(streams);
    return (
        <GroupSection label={label} count={streams.length} belowBorder>
            {streams.length === 0 && emptyText && <StatusLine>{emptyText}</StatusLine>}
            {streams.map((stream) => (
                <DisplayedRow
                    key={stream.id}
                    stream={stream}
                    text={texts.get(stream.id) ?? { measurement: stream.name, detail: "" }}
                    onRemove={addedIds.has(stream.id) ? onRemove : undefined}
                />
            ))}
        </GroupSection>
    );
}

function DisplayedRow({
    stream,
    text,
    onRemove,
}: {
    stream: PickerStream;
    text: PickerRowText;
    onRemove?: (streamId: number) => void;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const { measurement: name, detail } = text;
    return (
        <div className={ROW_CLASS}>
            <span
                aria-hidden
                className={"h-[4px] w-[14px] shrink-0 rounded-[2px]"}
                style={{ backgroundColor: stableStreamColor(stream.id) }}
            />
            <RowText stream={stream} text={text} />
            {onRemove && (
                <Button
                    aria-label={detail ? t("remove-row", { name, detail }) : t("remove-row-without-detail", { name })}
                    onClick={() => onRemove(stream.id)}
                    size={"icon-xs"}
                    variant={"outline"}
                >
                    <RemoveIcon />
                </Button>
            )}
        </div>
    );
}
